import { Indicator } from 'utils/models';
import { t } from 'i18next';
import { HeartRhythmMeasurementIndicatorMap } from 'models/heartRhythm/measurement';
import { MeasurementData } from 'models/measurement';
import { showUsaFeature } from './app';

export const HEARTRATE_LIMITS = {
  EU: { low: 40, normal: 110 },
  US: { min: 40, low: 50, normal: 100, max: 115 },
};

export const getHeartRhythmIndicatorColor = (indicator?: Indicator): string => {
  if (!indicator) return 'var(--mantine-color-gray-5)';
  return HeartRhythmMeasurementIndicatorMap[indicator]?.color;
};

export const defaultMeasurementIndicators = Object.values(Indicator).filter((indicator) => {
  const excludedIndicators = [Indicator.NoResult, Indicator.ResultUnavailable];
  if (!showUsaFeature) {
    excludedIndicators.push(Indicator.Inconclusive);
  }
  return !excludedIndicators.includes(indicator);
});

export const distributionMeasurementIndicators = () => {
  const indicatorValues = [
    Indicator.Normal,
    Indicator.Warning,
    Indicator.Urgent,
    Indicator.Inconclusive,
    Indicator.Quality,
  ];

  if (!showUsaFeature) {
    indicatorValues.splice(indicatorValues.indexOf(Indicator.Inconclusive), 1);
  }
  return indicatorValues;
};

export const getHeartrateColor = (heartrate?: number) => {
  if (!heartrate) return 'var(--mantine-color-peach-7)';
  const roundedHeartrate = Math.round(heartrate);

  const limits = showUsaFeature ? HEARTRATE_LIMITS.US : HEARTRATE_LIMITS.EU;
  return roundedHeartrate < limits.low || roundedHeartrate > limits.normal
    ? 'var(--mantine-color-peach-7)'
    : 'var(--mantine-color-turquoise-5)';
};

export const getHeartrateValueUsa = (indicator?: Indicator, heartrate?: number) => {
  if (
    !heartrate ||
    !indicator ||
    [Indicator.NoResult, Indicator.Quality, Indicator.ResultUnavailable].includes(indicator)
  )
    return '-';

  const roundedHeartrate = Math.round(heartrate);
  return roundedHeartrate;
};

export const getHeartrateValue = (heartrate?: number, indicator?: Indicator) => {
  if (!heartrate) return '-';

  if (
    showUsaFeature &&
    (!indicator || [Indicator.NoResult, Indicator.Quality, Indicator.ResultUnavailable].includes(indicator))
  ) {
    return '-';
  }
  const roundedHeartrate = Math.round(heartrate);
  return roundedHeartrate > 0 ? roundedHeartrate : '-';
};

const getHeartrateLabelUsa = (heartrate?: number) => {
  if (!heartrate) return `<${HEARTRATE_LIMITS.US.min} ${t('bpm_label')}`;
  const roundedHeartrate = Math.round(heartrate);
  const label = `${roundedHeartrate} ${t('bpm_label')} -`;
  if (roundedHeartrate < HEARTRATE_LIMITS.US.min) return `<${HEARTRATE_LIMITS.US.min} ${t('bpm_label')}`;
  if (roundedHeartrate < HEARTRATE_LIMITS.US.low) return `${label} ${t('heartrate_low_label')}`;
  if (roundedHeartrate <= HEARTRATE_LIMITS.US.normal) return `${label} ${t('heartrate_normal_label')}`;
  if (roundedHeartrate <= HEARTRATE_LIMITS.US.max) return `${label} ${t('heartrate_elevated_label')}`;
  return `>${HEARTRATE_LIMITS.US.max} ${t('bpm_label')}`;
};

export const getHeartrateLabel = (heartrate?: number) => {
  if (showUsaFeature) return getHeartrateLabelUsa(heartrate);
  const label = `${getHeartrateValue(heartrate)} ${t('bpm_label')} -`;
  if (!heartrate) return `${label} ${t('heartrate_low_label')}`;
  const roundedHeartrate = Math.round(heartrate);
  if (roundedHeartrate < HEARTRATE_LIMITS.EU.low) return `${label} ${t('heartrate_low_label')}`;
  if (roundedHeartrate <= HEARTRATE_LIMITS.EU.normal) return `${label} ${t('heartrate_normal_label')}`;
  return `${label} ${t('heartrate_elevated_label')}`;
};

export const getUnreviewedMeasurementLabel = (indicator?: Indicator) => {
  switch (indicator) {
    case Indicator.Urgent:
      return t('diagnosis_possible_atrial_fibrillation');
    case Indicator.Warning:
      return t('diagnosis_possibly_irregular');
    case Indicator.Normal:
      return t('diagnosis_regular');
    case Indicator.Quality:
      return t('diagnosis_quality_too_low');
    default:
      return indicator;
  }
};
export const getHeartRhythmIndicatorLabel = (indicator?: Indicator) => {
  if (!indicator) return t('indicator_label_no_result');
  return t(`indicator_label_${indicator}`);
};

export const showDeviceVersion = (data?: MeasurementData) => {
  if (!data?.app?.version) return '-';
  return `v${data.app.version}`;
};

export const showDeviceInfo = (data?: MeasurementData) => {
  if (!data?.device) return '-';
  return `${data.device.manufacturer} ${data.device.model}`;
};
